// src/scenes/GroupDashboard/index.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  Button,
  IconButton,
  Divider,
  List,
  Menu,
  MenuItem,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import MoneyIcon from "@mui/icons-material/Money";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { MoreVert } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { fetchExpensesData } from "../../../src/data/expensesData";
import { companyManagementUrl, userManagementUrl } from "../../config";
import {
  CustomersCard,
  ExpensesCard,
  RevenueCard,
  RevenueTrend,
  SalesCard,
  SalesTrend,
  SoldProductsChart,
} from "../../data/Axios/queries";
import { graphqlQuery } from "../../data/Axios/DynamicService";
import { tokens } from "../../theme";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../context/TourContext";

const base_url = companyManagementUrl.uri;

// Define tour steps for GroupDashboard
const groupDashboardTourSteps = [
  {
    target: ".group-dashboard-container",
    content: "Welcome to the Group Dashboard! Monitor group-based lending metrics, including portfolio, performance, collections, and risk, using filters for regions, branches, and sales reps.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: ".group-dashboard-region-filter",
    content: "Use the Region filter to view group lending data for a specific region. Available for Admins, Regional Managers, and Branch Managers.",
    placement: "bottom",
    condition: (props) => [1, 2, 3].includes(props.userRole),
  },
  {
    target: ".group-dashboard-branch-filter",
    content: "Select a Branch to analyze group lending metrics specific to that branch.",
    placement: "bottom",
    condition: (props) => [1, 2, 3].includes(props.userRole),
  },
  {
    target: ".group-dashboard-sales-rep-filter",
    content: "Choose a Sales Rep (Field Officer) to monitor their group lending performance.",
    placement: "bottom",
    condition: (props) => [1, 2, 3, 4].includes(props.userRole),
  },
  {
    target: ".group-dashboard-clear-all",
    content: "Click 'Clear All' to reset all filters and view default group lending data.",
    placement: "bottom",
    condition: (props) => props.userRole !== 5,
  },
  {
    target: ".group-dashboard-portfolio-card",
    content: "The Portfolio card displays key group metrics like Active Loans, Total Groups, and Outstanding Loan Balance (OLB).",
    placement: "top",
  },
  {
    target: ".group-dashboard-performance-card",
    content: "The Performance card shows group funding percentage, new groups, disbursed loans, and collection rate for the month.",
    placement: "top",
  },
  {
    target: ".group-dashboard-collection-card",
    content: "The Collection card highlights today’s group collection metrics, including Collection Rate, Loans Due, and Prepaid Loans.",
    placement: "top",
  },
  {
    target: ".group-dashboard-risk-card",
    content: "The Risk card provides insights into group portfolio risk, including Portfolio at Risk, Total Arrears, and Non-Performing Loans (NPL) breakdown.",
    placement: "top",
  },
];

const GroupDashboard = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const colors = tokens(theme.palette.mode);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const user = parseInt(decodedToken.RoleGroupId);
  const [branchData, setBranchData] = useState([]);
  const [fieldOfficerfData, setFieldOfficerData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [regionId, setRegionId] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [fieldOfficerId, setFieldOfficerId] = useState(null);
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const circleSize = isSmallScreen ? 150 : 200;

  // Tour state
  const { isFirstTime, touredPages, markPageAsToured } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const currentPath = window.location.pathname;

  useEffect(() => {
    if (isFirstTime && !touredPages.includes(currentPath) && !loading) {
      setShowModal(true);
    }
  }, [isFirstTime, touredPages, currentPath, loading]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      markPageAsToured(currentPath);
      setShowModal(false);
    }
  };

  const startTour = () => {
    setRunTour(true);
    setShowModal(false);
  };

  const cancelTour = () => {
    setShowModal(false);
    markPageAsToured(currentPath);
  };

  // Fetch data on component mount
  useEffect(() => {
    async function fetchAllData() {
      try {
        setLoading(true);
        const regionDataResponse = await fetchJsonDynamic("region", null, null, base_url);
        if (regionDataResponse?.results) {
          const results = JSON.parse(regionDataResponse.results);
          setRegionData(results || []);
        }

        const branchDataResponse = await fetchJsonDynamic("branch", null, null, base_url);
        if (branchDataResponse?.results) {
          const results = JSON.parse(branchDataResponse.results);
          setBranchData(results || []);
        }

        const fieldOfficerDataResponse = await fetchJsonDynamic(
          "allUsersView",
          null,
          null,
          userManagementUrl.uri
        );
        if (fieldOfficerDataResponse?.results) {
          const results = JSON.parse(fieldOfficerDataResponse.results);
          const fieldOfficersWithRole5 = results.filter((user) => user.RoleGroupId === 5);
          setFieldOfficerData(fieldOfficersWithRole5 || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const dashboardDataResponse = await fetchJsonDynamic(
        "groupDashboardMetricsById",
        "dashboardMetrics",
        {
          companyid: null,
          regionid: regionId,
          branchid: branchId,
          fieldofficerid: fieldOfficerId,
        },
        base_url
      );
      if (dashboardDataResponse?.results) {
        const results = JSON.parse(dashboardDataResponse.results);
        setDashboardData(results || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [regionId, branchId, fieldOfficerId]);

  const paperStyle = {
    p: 1,
    display: "flex",
    alignItems: "center",
    height: 50,
    backgroundColor: `${colors.primary[400]} !important`,
  };

  const selectStyle = {
    height: "100%",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      padding: "8px",
    },
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    backgroundColor: `${colors.primary[500]} !important`,
    color: "white",
    padding: "0 10px",
    borderRadius: "4px",
    height: "100%",
    marginRight: "10px",
  };

  const regionOptions = regionData.map((region) => ({
    value: region.Id,
    label: region.Name,
  }));

  const branchOptions = branchData
    .filter((branch) => !regionId || branch.RegionId === regionId)
    .map((branch) => ({
      value: branch.Id,
      label: branch.Name,
    }));

  const fieldOfficerOptions = fieldOfficerfData
    .filter((officer) => !branchId || officer.BranchId === branchId)
    .map((officer) => ({
      value: officer.Id,
      label: `${officer.FirstName} ${officer.LastName}`,
    }));

  const handleRegionChange = (event) => {
    const selectedRegionId = event.target.value;
    setRegionId(selectedRegionId);
    setBranchId(null);
    setFieldOfficerId(null);
  };

  const handleBranchChange = (event) => {
    const selectedBranchId = event.target.value;
    setBranchId(selectedBranchId);
    const selectedBranch = branchData.find((branch) => branch.Id === selectedBranchId);
    if (selectedBranch) {
      setRegionId(selectedBranch.RegionId);
    }
    setFieldOfficerId(null);
  };

  const handleFieldOfficerChange = (event) => {
    const selectedFieldOfficerId = event.target.value;
    setFieldOfficerId(selectedFieldOfficerId);
    const selectedOfficer = fieldOfficerfData.find((officer) => officer.Id === selectedFieldOfficerId);
    if (selectedOfficer) {
      setBranchId(selectedOfficer.BranchId);
      const selectedBranch = branchData.find((branch) => branch.Id === selectedOfficer.BranchId);
      if (selectedBranch) {
        setRegionId(selectedBranch.RegionId);
      }
    }
  };

  const handleClearAll = () => {
    setRegionId(null);
    setBranchId(null);
    setFieldOfficerId(null);
    fetchDashboardData();
  };

  const getProgressColor = (value) => {
    if (value <= 40) return "green";
    if (value <= 60) return "#d4a106";
    return "red";
  };

  const getPositiveProgressColor = (value) => {
    if (value <= 40) return "red";
    if (value <= 60) return "#d4a106";
    return "green";
  };

  return (
    <Box
      sx={{
        p: 3,
        display: "grid",
        gridTemplateColumns: "1fr",
        gridGap: "16px",
      }}
      className="group-dashboard-container"
    >
      {/* Joyride Tour */}
      <Joyride
        steps={groupDashboardTourSteps.filter((step) =>
          step.condition ? step.condition({ userRole: user }) : true
        )}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: colors.blueAccent[500],
            backgroundColor: colors.primary[400],
            textColor: colors.grey[100],
            zIndex: 1000,
          },
        }}
      />

      {/* First-Time Modal */}
      <Dialog open={showModal} onClose={cancelTour}>
        <DialogTitle>Welcome to the Group Dashboard!</DialogTitle>
        <DialogContent>
          This dashboard helps you monitor group-based lending metrics. Would you like a guided tour to explore its features?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelTour}>Skip</Button>
          <Button onClick={startTour} variant="contained">
            Start Tour
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Grid container spacing={4}>
          {/* Filters */}
          <Grid container spacing={4} className="group-dashboard-filters">
            {(user === 1 || user === 2 || user === 3) && regionData.length > 0 && (
              <Grid item xs={12} md={3} className="group-dashboard-region-filter">
                <Paper elevation={3} sx={paperStyle}>
                  <Typography sx={labelStyle}>
                    <FilterAltIcon style={{ marginRight: "5px" }} />
                    Region
                  </Typography>
                  <FormControl sx={{ flex: 1, height: "100%" }}>
                    <InputLabel>Select Region...</InputLabel>
                    <Select
                      label="Select Region..."
                      value={regionId || ""}
                      onChange={handleRegionChange}
                      sx={selectStyle}
                      className="group-dashboard-region-select"
                      aria-label="Region Filter"
                    >
                      {regionOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            )}

            {(user === 1 || user === 2 || user === 3) && branchData.length > 0 && (
              <Grid item xs={12} md={4} className="group-dashboard-branch-filter">
                <Paper elevation={3} sx={paperStyle}>
                  <Typography sx={labelStyle}>
                    <FilterAltIcon style={{ marginRight: "5px" }} />
                    Branches
                  </Typography>
                  <FormControl sx={{ flex: 1, height: "100%" }}>
                    <InputLabel>Select Branch...</InputLabel>
                    <Select
                      label="Select Branch..."
                      value={branchId || ""}
                      onChange={handleBranchChange}
                      sx={selectStyle}
                      className="group-dashboard-branch-select"
                      aria-label="Branch Filter"
                    >
                      {branchOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            )}

            {(user === 1 || user === 2 || user === 3 || user === 4) && fieldOfficerfData.length > 0 && (
              <Grid item xs={12} md={4} className="group-dashboard-sales-rep-filter">
                <Paper elevation={3} sx={paperStyle}>
                  <Typography sx={labelStyle}>
                    <FilterAltIcon style={{ marginRight: "5px" }} />
                    Sales Reps
                  </Typography>
                  <FormControl sx={{ flex: 1, height: "100%" }}>
                    <InputLabel>Select Sales Rep...</InputLabel>
                    <Select
                      label="Select Sales Rep..."
                      value={fieldOfficerId || ""}
                      onChange={handleFieldOfficerChange}
                      sx={selectStyle}
                      className="group-dashboard-sales-rep-select"
                      aria-label="Sales Rep Filter"
                    >
                      {fieldOfficerOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            )}

            {user !== 5 && (
              <Grid item xs={12} md={1} className="group-dashboard-clear-all">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClearAll}
                >
                  Clear All
                </Button>
              </Grid>
            )}
          </Grid>

          {/* Portfolio Overview Card */}
          <Grid item xs={12} md={6} className="group-dashboard-portfolio-card">
            <Paper
              elevation={3}
              sx={{
                p: 2,
                height: 400,
                background: `${colors.primary[400]} !important`,
                display: "flex",
                flexDirection: "column",
              }}
              role="region"
              aria-label="Portfolio Overview"
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: "white",
                    px: 2,
                    py: 1,
                    borderRadius: "0px 10px 10px 0px",
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="h6">Portfolio</Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    ml: 2,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    background: `${colors.primary[200]} !important`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%", borderBottom: `1px solid ${theme.palette.primary.main}` }}>
                      <IconButton sx={{ backgroundColor: theme.palette.primary.main, color: "white", mr: 2, p: 1, borderRadius: 0 }}>
                        <AccountBalanceIcon />
                      </IconButton>
                      <Typography variant="h5">Active Loans</Typography>
                    </Box>
                    <Typography variant="h2" sx={{ alignSelf: "flex-end" }}>
                      {dashboardData[0]?.GroupActiveLoans || 0}
                    </Typography>
                  </Box>
                </Paper>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    background: `${colors.primary[200]} !important`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%", borderBottom: `1px solid ${theme.palette.primary.main}` }}>
                      <IconButton sx={{ backgroundColor: theme.palette.primary.main, color: "white", mr: 2, p: 1, borderRadius: 0 }}>
                        <PeopleIcon />
                      </IconButton>
                      <Typography variant="h5">Total Groups</Typography>
                    </Box>
                    <Typography variant="h2" sx={{ alignSelf: "flex-end" }}>
                      {dashboardData[0]?.GroupTotalCustomers || 0}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  background: `${colors.primary[200]} !important`,
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", width: "100%", borderBottom: `1px solid ${theme.palette.primary.main}` }}>
                    <IconButton sx={{ backgroundColor: theme.palette.primary.main, color: "white", mr: 2, p: 1, borderRadius: 0 }}>
                      <MoneyIcon />
                    </IconButton>
                    <Typography variant="h5">OLB(KSH)</Typography>
                  </Box>
                  <Typography variant="h2" sx={{ alignSelf: "flex-end" }}>
                    KSH {dashboardData[0]?.GroupOutstandingLoanBalance || 0}
                  </Typography>
                </Box>
              </Paper>
            </Paper>
          </Grid>

          {/* Performance Card */}
          <Grid item xs={12} md={6} className="group-dashboard-performance-card">
            <Paper
              elevation={3}
              sx={{
                p: 2,
                height: 400,
                background: `${colors.primary[400]} !important`,
                display: "flex",
                flexDirection: "column",
              }}
              role="region"
              aria-label="Performance Metrics"
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: "white",
                    px: 2,
                    py: 1,
                    borderRadius: "0px 10px 10px 0px",
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="h6">Performance</Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    ml: 2,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "50%" }}>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={circleSize}
                      thickness={7}
                      sx={{ color: "rgba(255, 255, 255, 0.4)", position: "absolute" }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={parseFloat(dashboardData[0]?.GroupFundedPercentage?.toFixed(2)) || 0}
                      size={circleSize}
                      thickness={7}
                      sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.GroupFundedPercentage?.toFixed(2)) || 0) }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.GroupFundedPercentage?.toFixed(2)) || 0) }}
                      >
                        Funded
                      </Typography>
                      <Typography
                        variant="h4"
                        component="div"
                        sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.GroupFundedPercentage?.toFixed(2)) || 0) }}
                      >
                        {parseFloat(dashboardData[0]?.GroupFundedPercentage?.toFixed(2)) || 0}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ width: "50%" }}>
                  <List sx={{ p: 0 }}>
                    <ListItemButton divider>
                      <ListItemText primary="New Groups" />
                      <Typography
                        variant="h6"
                        sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px" }}
                      >
                        {dashboardData[0]?.New_Customers || 0}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Disbursed Loans" />
                      <Typography
                        variant="h6"
                        sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px" }}
                      >
                        {dashboardData[0]?.GroupDisbursedLoans || 0}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Disbursed Amount" />
                      <Typography
                        variant="h6"
                        sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px" }}
                      >
                        {dashboardData[0]?.GroupDisbursedAmount || 0}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="CR MTD" />
                      <Typography
                        variant="h6"
                        sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px" }}
                      >
                        {parseFloat(dashboardData[0]?.GroupCollectionRateMtdPercentage?.toFixed(2)) || 0}%
                      </Typography>
                    </ListItemButton>
                  </List>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Collection Card */}
          <Grid item xs={12} md={6} className="group-dashboard-collection-card">
            <Paper
              elevation={3}
              sx={{
                p: 2,
                height: 580,
                background: `${colors.primary[400]} !important`,
                display: "flex",
                flexDirection: "column",
              }}
              role="region"
              aria-label="Collection Metrics"
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: "white",
                    px: 2,
                    py: 1,
                    borderRadius: "0px 10px 10px 0px",
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="h6">Collection</Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    ml: 2,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", mt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "50%" }}>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={circleSize}
                      thickness={7}
                      sx={{ color: "rgba(255, 255, 255, 0.4)", position: "absolute" }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={parseFloat(dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)) || 0}
                      size={circleSize}
                      thickness={7}
                      sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)) || 0) }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)) || 0) }}
                      >
                        Collection Rate
                      </Typography>
                      <Typography
                        variant="h2"
                        component="div"
                        sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)) || 0) }}
                      >
                        {parseFloat(dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)) || 0}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ width: "50%", display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1, p: 1 }}>
                    <Box sx={{ background: theme.palette.primary.main, color: "white", p: 0.5, borderRadius: 1 }}>
                      <Typography variant="h5">Loans Due Today</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      {dashboardData[0]?.GroupCountLoansDueToday || 0} Loan
                    </Typography>
                    <Typography variant="h4">
                      KSH {dashboardData[0]?.GroupLoansDueToday || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1, p: 1 }}>
                    <Box sx={{ background: theme.palette.primary.main, color: "white", p: 0.5, borderRadius: 1 }}>
                      <Typography variant="h5">Collected</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      {dashboardData[0]?.GroupCountCollectedToday || 0} Loans
                    </Typography>
                    <Typography variant="h4">
                      KSH {dashboardData[0]?.GroupCollectedToday || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1, p: 1 }}>
                    <Box sx={{ background: theme.palette.primary.main, color: "white", p: 0.5, borderRadius: 1 }}>
                      <Typography variant="h5">Prepaid Loans</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      {dashboardData[0]?.GroupCountPrepaidLoansToday || 0} Loans
                    </Typography>
                    <Typography variant="h4">
                      KSH {dashboardData[0]?.GroupPrepaidLoansToday || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Risk Card */}
          <Grid item xs={12} md={6} className="group-dashboard-risk-card">
            <Paper
              elevation={3}
              sx={{ p: 3, background: `${colors.primary[400]} !important` }}
              role="region"
              aria-label="Risk Metrics"
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: "white",
                    px: 2,
                    py: 1,
                    borderRadius: "0px 10px 10px 0px",
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="h6">Risk</Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    ml: 2,
                  }}
                />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={2}
                    sx={{ p: 2, backgroundColor: `${theme.palette.background.default}`, display: "flex", flexDirection: "column", alignItems: "center", height: 200 }}
                  >
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={isSmallScreen ? 100 : 150}
                        thickness={6}
                        sx={{ color: "rgba(255, 255, 255, 0.4)", position: "absolute" }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={parseFloat(dashboardData[0]?.GroupPortfolioAtRisk?.toFixed(2)) || 0}
                        size={isSmallScreen ? 100 : 150}
                        thickness={6}
                        sx={{ color: getProgressColor(parseFloat(dashboardData[0]?.GroupPortfolioAtRisk?.toFixed(2)) || 0) }}
                      />
                      <Box
                        sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: "absolute",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ color: getProgressColor(parseFloat(dashboardData[0]?.GroupPortfolioAtRisk?.toFixed(2)) || 0) }}
                        >
                          % Portfolio At Risk
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{ color: getProgressColor(parseFloat(dashboardData[0]?.GroupPortfolioAtRisk?.toFixed(2)) || 0) }}
                        >
                          {parseFloat(dashboardData[0]?.GroupPortfolioAtRisk?.toFixed(2)) || 0}%
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={2}
                    sx={{ backgroundColor: `${theme.palette.background.default}`, display: "flex", flexDirection: "column", height: 200 }}
                  >
                    <List sx={{ p: 0 }}>
                      {[
                        { label: "Total Arrears", value: dashboardData[0]?.GroupTotalArrears || 0 },
                        { label: "Arrears Collected Today", value: dashboardData[0]?.GroupArrearsCollectedToday || 0 },
                        { label: "NPL Amount Collected", value: dashboardData[0]?.GroupNplAmountCollected || 0 },
                        { label: "RollOver Loans", value: dashboardData[0]?.GroupRollOverLoans || 0 },
                      ].map((item, idx) => (
                        <ListItemButton divider key={idx} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "flex-start" }}>
                            <Typography variant="subtitle2" color="textSecondary">
                              {item.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px", alignSelf: "flex-end" }}
                            >
                              KSH {item.value}
                            </Typography>
                          </Box>
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Paper elevation={2} sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
                  <Typography variant="h6" gutterBottom>
                    Non-Performing Loans Breakdown
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { title: "Principal Amount in NPL", value: `KSH ${dashboardData[0]?.GroupNplPrincipalAmount || 0}` },
                      { title: "NPL Amount", value: `KSH ${dashboardData[0]?.GroupNplLoanBalance || 0}` },
                      { title: "Number of NPLs", value: dashboardData[0]?.GroupCountNplLoanBalance || 0 },
                      { title: "Percentage Turnover", value: `${parseFloat(dashboardData[0]?.GroupTurnoverPercentage?.toFixed(2)) || 0}%` },
                    ].map((item, idx) => (
                      <Grid item xs={12} sm={6} md={6} key={idx}>
                        <Box sx={{ p: 2, border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1, textAlign: "center" }}>
                          <Typography variant="h6" color="textSecondary">
                            {item.title}
                          </Typography>
                          <Typography variant="h4">{item.value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default GroupDashboard;